/**
 * Phone side of QR device login (/pair/:code).
 *
 * The logged-in user confirms logging the TV in to their account, then waits
 * until the TV creates a session and goes to /join/{session_code}.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { BrandLogo } from '@/components/low/BrandLogo'
import { useAuthStore } from '@/stores/authStore'
import { approveDevicePairing, getDevicePairingSession } from '@/services/api'
import { connectPairingSocket } from '@/services/pairingSocket'

type PairStatus = 'confirm' | 'approving' | 'waiting' | 'error'

export function PairDevicePage() {
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const username = useAuthStore((s) => s.user?.username)
  const [status, setStatus] = useState<PairStatus>('confirm')
  // Read inside socket handlers registered once per mount
  const approvedRef = useRef(false)

  useEffect(() => {
    if (!code) return
    let cancelled = false
    let disconnect: (() => void) | null = null

    const goToSession = (sessionCode: string) => {
      if (cancelled) return
      cancelled = true
      disconnect?.()
      navigate(`/join/${sessionCode}`, { replace: true })
    }

    disconnect = connectPairingSocket(code, {
      onConnected: () => {
        if (cancelled || !approvedRef.current) return
        // session_ready may have been emitted while disconnected
        getDevicePairingSession(code)
          .then(({ session_code }) => {
            if (session_code !== null) goToSession(session_code)
          })
          .catch((error) => console.error('[PairDevice] Session fallback failed:', error))
      },
      onApproved: () => {},
      onSessionReady: goToSession,
    })

    return () => {
      cancelled = true
      disconnect?.()
    }
  }, [code, navigate])

  const handleApprove = async () => {
    if (!code) return
    setStatus('approving')
    try {
      await approveDevicePairing(code)
      approvedRef.current = true
      setStatus('waiting')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4 py-8 gap-6">
      <BrandLogo className="h-8" />
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 flex flex-col gap-6 text-center">
          {(status === 'confirm' || status === 'approving') && (
            <>
              <p className="text-lg">{t('pair.confirmQuestion', { username })}</p>
              <Button
                onClick={handleApprove}
                disabled={status === 'approving'}
                className="w-full min-h-[44px]"
                data-testid="pair-approve"
              >
                {t('pair.approve')}
              </Button>
            </>
          )}
          {status === 'waiting' && (
            <p className="text-muted-foreground animate-pulse" data-testid="pair-waiting">
              {t('pair.waitingForSession')}
            </p>
          )}
          {status === 'error' && (
            <p className="text-destructive" data-testid="pair-error">
              {t('pair.expired')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
